import {
  BellRing,
  BriefcaseBusiness,
  Building2,
  CalendarClock,
  CalendarRange,
  HandCoins,
  History,
  IdCard,
  KeyRound,
  Landmark,
  LayoutGrid,
  LockKeyhole,
  MapPin,
  MonitorSmartphone,
  Network,
  ScrollText,
  ShieldCheck,
  UserRound,
  UsersRound,
  Workflow,
  type LucideIcon,
} from "lucide-react";

import {
  SettingsAccessImg,
  SettingsCalendarsImg,
  SettingsNotificationsImg,
  SettingsOrganizationImg,
  SettingsPoliciesImg,
  SettingsProfileImg,
  SettingsSecurityImg,
} from "@/assets/images";
import { COMPANY_SETTINGS_PATHS } from "@/routes/company/routes.settings";
import { MS_SETTINGS_PATHS } from "@/routes/my-space/routes.settings";

/**
 * The sidebar of the main Settings screens (My Space and Workspace).
 *
 * Each section page (`/settings/security-settings`, …) used to draw its own
 * vertical rail of sub-tabs under a horizontal tab bar. Both levels now live
 * here, in one sidebar shaped like the main menu: a group is a section — an
 * expandable row, like a module with page tabs — and an item is one of its
 * sub-tabs (`?vTab=`). The section page renders the active panel with its own
 * rail hidden.
 *
 * URLs are unchanged, so global search, the profile drawer and old bookmarks
 * keep resolving. An item's `to` must be a `VTAB_*` path — its `vTab` value is
 * what the section page's `VerticalTabs` selects on, so it has to match the
 * `value` in that page's tab list.
 *
 * Only the two main Settings pages use this. Module settings (Leave, Payroll,
 * …) keep their own in-module layout.
 */
export interface ISettingsNavItem {
  /** i18n key (`ns:key`). */
  label: string;
  icon: LucideIcon;
  to: string;
  /**
   * Path prefixes of full-page screens that belong to this item (a create or
   * edit form with its own route) — the item stays highlighted on them. The
   * longest matching prefix wins.
   */
  activeFor?: string[];
  /**
   * Extra terms the sidebar's filter matches, on top of the item's and its
   * group's translated labels. Search data, never rendered — so English-only
   * synonyms ("2fa", "iban") are fine here.
   */
  keywords?: string[];
}

export interface ISettingsNavGroup {
  /** i18n key (`ns:key`). */
  label: string;
  /**
   * URL of the group row's icon image (drawn by `NavIcon`, as the main menu
   * draws a module's uploaded icon) — its only mark on the collapsed rail.
   */
  iconSrc: string;
  items: ISettingsNavItem[];
}

export interface ISettingsNav {
  space: "myspace" | "company";
  groups: ISettingsNavGroup[];
}

export const MY_SPACE_SETTINGS_NAV: ISettingsNav = {
  space: "myspace",
  groups: [
    {
      label: "nav:settings_nav.groups.profile",
      iconSrc: SettingsProfileImg,
      items: [
        { label: "nav:tabs.personal_information", icon: UserRound, to: MS_SETTINGS_PATHS.VTAB_PROFILE_PERSONAL, keywords: ["name", "photo", "avatar", "contact", "address", "phone"] },
        { label: "nav:tabs.employment_information", icon: BriefcaseBusiness, to: MS_SETTINGS_PATHS.VTAB_PROFILE_EMPLOYMENT, keywords: ["job", "manager", "joining", "contract"] },
        { label: "nav:tabs.compensation_information", icon: HandCoins, to: MS_SETTINGS_PATHS.VTAB_PROFILE_COMPENSATION, keywords: ["salary", "pay", "allowance"] },
        { label: "nav:tabs.banking_details", icon: Landmark, to: MS_SETTINGS_PATHS.VTAB_PROFILE_BANKING, keywords: ["bank", "iban", "account"] },
      ],
    },
    {
      label: "nav:settings_nav.groups.security",
      iconSrc: SettingsSecurityImg,
      items: [
        { label: "nav:tabs.password", icon: LockKeyhole, to: MS_SETTINGS_PATHS.VTAB_SECURITY_PASSWORD, keywords: ["change password", "reset"] },
        { label: "nav:tabs.multi_factor_authentication", icon: KeyRound, to: MS_SETTINGS_PATHS.VTAB_SECURITY_MFA, keywords: ["2fa", "mfa", "two factor", "authenticator", "backup codes"] },
        { label: "nav:tabs.active_sessions", icon: MonitorSmartphone, to: MS_SETTINGS_PATHS.VTAB_SECURITY_ACTIVE_SESSIONS, keywords: ["devices", "sign out", "logout"] },
        { label: "nav:tabs.login_history", icon: History, to: MS_SETTINGS_PATHS.VTAB_SECURITY_LOGIN_HISTORY, keywords: ["sign in", "activity"] },
      ],
    },
    {
      label: "nav:settings_nav.groups.notifications",
      iconSrc: SettingsNotificationsImg,
      items: [
        { label: "nav:tabs.notifications_method", icon: BellRing, to: MS_SETTINGS_PATHS.VTAB_NOTIFICATIONS_METHOD, keywords: ["email", "push", "sound", "alerts"] },
      ],
    },
  ],
};

export const WORKSPACE_SETTINGS_NAV: ISettingsNav = {
  space: "company",
  groups: [
    {
      label: "nav:settings_nav.groups.organization",
      iconSrc: SettingsOrganizationImg,
      items: [
        { label: "nav:tabs.company_information", icon: Building2, to: COMPANY_SETTINGS_PATHS.VTAB_ORG_COMPANY_INFO, keywords: ["logo", "brand", "company name", "trade license"] },
        { label: "nav:tabs.branches", icon: MapPin, to: COMPANY_SETTINGS_PATHS.VTAB_ORG_BRANCHES, keywords: ["offices", "locations"] },
        { label: "nav:tabs.departments", icon: Network, to: COMPANY_SETTINGS_PATHS.VTAB_ORG_DEPARTMENTS, keywords: ["divisions", "teams"] },
        { label: "nav:tabs.positions_or_designations", icon: IdCard, to: COMPANY_SETTINGS_PATHS.VTAB_ORG_POSITIONS, keywords: ["job titles", "designations"] },
        { label: "nav:tabs.organizational_structure", icon: Workflow, to: COMPANY_SETTINGS_PATHS.VTAB_ORG_STRUCTURE, keywords: ["org chart", "hierarchy"] },
      ],
    },
    {
      label: "nav:settings_nav.groups.access",
      iconSrc: SettingsAccessImg,
      items: [
        { label: "nav:tabs.roles_and_permissions", icon: ShieldCheck, to: COMPANY_SETTINGS_PATHS.VTAB_ROLES_LIST, keywords: ["rbac", "access", "permissions"] },
        { label: "company:roles.user_roles", icon: UsersRound, to: COMPANY_SETTINGS_PATHS.VTAB_ROLES_USER_ROLES, keywords: ["assigned roles", "who has access"] },
      ],
    },
    {
      label: "nav:settings_nav.groups.policies",
      iconSrc: SettingsPoliciesImg,
      items: [
        // The policy and category forms are routes of their own
        // (routes.company-policies-settings.ts — not imported here, it
        // eager-loads a page component) nested under this section's path.
        {
          label: "nav:tabs.categories",
          icon: LayoutGrid,
          to: COMPANY_SETTINGS_PATHS.VTAB_POLICIES_CATEGORIES,
          activeFor: [`${COMPANY_SETTINGS_PATHS.TAB_COMPANY_POLICIES}/category`],
          keywords: ["policy groups"],
        },
        {
          label: "nav:tabs.policies",
          icon: ScrollText,
          to: COMPANY_SETTINGS_PATHS.VTAB_POLICIES_LIST,
          activeFor: [`${COMPANY_SETTINGS_PATHS.TAB_COMPANY_POLICIES}/`],
          keywords: ["handbook", "rules"],
        },
      ],
    },
    {
      label: "nav:settings_nav.groups.calendars",
      iconSrc: SettingsCalendarsImg,
      items: [
        { label: "nav:tabs.holiday_types", icon: CalendarClock, to: COMPANY_SETTINGS_PATHS.VTAB_CAL_TYPES, keywords: ["calendar types"] },
        { label: "nav:tabs.holiday_calendar", icon: CalendarRange, to: COMPANY_SETTINGS_PATHS.VTAB_CAL_HOLIDAYS, keywords: ["public holidays", "working days"] },
      ],
    },
    // Templates (COMPANY_SETTINGS_PATHS.TAB_TEMPLATES) is deliberately absent,
    // as it was from the old tab bar — document templates are managed from the
    // Documents module. Its route still renders inside this layout, with its
    // own rail, for deep links and global search.
  ],
};
